import React from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
    FormControlLabel, FormGroup, Grid, MenuItem, TextField, ToggleButton,
    ToggleButtonGroup, Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { DataAPIContext, GET_ONE, CREATE } from '../api';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import DateRangePicker from '../components/DateRangePicker';
import LicenseeFormField from '../components/LicenseeFormField';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { DateTimePicker } from '../components/TimePicker';
import { ContextStore } from '../store/contextStore';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 600,
    },
}));

// Above this many order IDs the chip field collapses into a summary.
const IDS_COLLAPSE_THRESHOLD = 100;

// Presets for the updated/created windows ("updated in the last N hours").
const UPDATED_PRESETS = [
    { label: 'Last hour', hours: 1 },
    { label: 'Last 3h', hours: 3 },
    { label: 'Last 24h', hours: 24 },
    { label: 'Last 7 days', hours: 168 },
];

// Travel-time groups for reconcile mode (orders with no dated bookings
// count as past). Default selection: active + future.
const RECONCILE_GROUPS = [
    { value: 'past', label: 'Past — travel ended' },
    { value: 'active', label: 'Active — travel in progress' },
    { value: 'future', label: 'Future — travel not started' },
];

// Source modes, each gated on the integration's ORDER_QUERY capabilities.
const SOURCE_MODES = [
    { value: 'updated', label: 'Updated window', cap: (oq) => Boolean(oq?.updated_range) },
    { value: 'created', label: 'Created window', cap: (oq) => Boolean(oq?.created_range) },
    { value: 'products', label: 'Products + travel dates', cap: (oq) => Boolean(oq?.by_product_travel_date) },
    { value: 'orders', label: 'Order IDs', cap: (oq) => Boolean(oq?.by_ids) },
    { value: 'reconcile', label: 'Reconcile existing', cap: (oq) => Boolean(oq?.reconcile_existing) },
];

const SyncOrdersPage = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);

    const [selectedLicensee, setSelectedLicensee] = React.useState(currentLicensee || null);
    const effectiveTopId = isFranchise && selectedLicensee ? selectedLicensee : null;

    const [integrations, setIntegrations] = React.useState([]);
    const [accounts, setAccounts] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const [integration, setIntegration] = React.useState('');
    const [account, setAccount] = React.useState(null);
    const [sourceMode, setSourceMode] = React.useState('');
    const [updatedRange, setUpdatedRange] = React.useState([null, null]);
    const [createdRange, setCreatedRange] = React.useState([null, null]);
    const [productIds, setProductIds] = React.useState('');
    const [travelRange, setTravelRange] = React.useState([moment().subtract(1, 'days'), moment()]);
    const [orderIds, setOrderIds] = React.useState([]);
    const [reconcileGroups, setReconcileGroups] = React.useState(['active', 'future']);
    const [dryRun, setDryRun] = React.useState(false);
    const [sendNotifications, setSendNotifications] = React.useState(false);
    const [sendSurveys, setSendSurveys] = React.useState(false);
    const [approveBeforeImport, setApproveBeforeImport] = React.useState(false);
    const [processingMode, setProcessingMode] = React.useState('queue');

    // How the selected integration can query orders (SYNC_ORDERS_QUERY).
    const oq = React.useMemo(
        () => integrations.find((i) => i.name === integration)?.order_query || null,
        [integrations, integration],
    );
    const availableModes = React.useMemo(
        () => SOURCE_MODES.filter((m) => m.cap(oq)),
        [oq],
    );

    // Fetch integration list on mount (and when licensee changes); auto-select if only one
    React.useEffect(() => {
        if (isFranchise && !selectedLicensee) return;
        const suffix = effectiveTopId ? `integration_list/?franchise_to=${effectiveTopId}` : 'integration_list/';
        dataAPI(GET_ONE, 'bg_tasks', { urlSuffix: suffix })
            .then((res) => {
                if (res && res.statusCode === 200) {
                    // Only integrations that support order sync as a UI-launchable
                    // background task (order_query present, ui_launchable !== false).
                    const list = (res.data || []).filter(
                        (i) => i.order_query && i.order_query.ui_launchable !== false,
                    );
                    setIntegrations(list);
                    setIntegration(list.length === 1 ? list[0].name : '');
                }
            })
            .catch(() => {});
    }, [dataAPI, effectiveTopId]);

    // Reset capability-dependent inputs when the integration changes.
    React.useEffect(() => {
        setUpdatedRange([null, null]);
        setCreatedRange([null, null]);
        setProductIds('');
        setOrderIds([]);
    }, [integration]);

    // Keep the source mode within the selected integration's capabilities.
    React.useEffect(() => {
        if (!availableModes.some((m) => m.value === sourceMode)) {
            setSourceMode(availableModes.length > 0 ? availableModes[0].value : '');
        }
    }, [availableModes, sourceMode]);

    // Fetch accounts when integration changes
    React.useEffect(() => {
        if (!integration) {
            setAccounts([]);
            setAccount(null);
            return;
        }
        setLoading(true);
        dataAPI(GET_ONE, 'bg_tasks', {
            urlSuffix: `integration_accounts/?integration=${encodeURIComponent(integration)}${effectiveTopId ? `&franchise_to=${effectiveTopId}` : ''}`,
        })
            .then((res) => {
                if (res && res.statusCode === 200) {
                    setAccounts(res.data || []);
                    setAccount(null);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dataAPI, integration]);

    // Merge a text file (one order ID per line) into the chips, deduped.
    const handleIdsFile = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // allow re-uploading the same file
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const lines = String(reader.result || '')
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean);
            if (!lines.length) {
                enqueueSnackbar('The file contains no order IDs.', { variant: 'warning' });
                return;
            }
            setOrderIds((prev) => {
                const merged = Array.from(new Set([...prev, ...lines]));
                enqueueSnackbar(
                    `Loaded ${merged.length - prev.length} new order ID(s) from ${file.name}.`,
                    { variant: 'success' });
                return merged;
            });
        };
        reader.onerror = () => enqueueSnackbar(`Could not read ${file.name}.`, { variant: 'error' });
        reader.readAsText(file);
    };

    const productIdList = productIds.trim()
        ? productIds.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
        : [];

    const sourceOk = (() => {
        switch (sourceMode) {
            case 'updated': return Boolean(updatedRange[0]);
            case 'created': return Boolean(createdRange[0]);
            case 'products': return Boolean(productIdList.length > 0 && travelRange[0] && travelRange[1]);
            case 'orders': return orderIds.length > 0;
            case 'reconcile': return !oq?.reconcile_groups || reconcileGroups.length > 0;
            default: return false;
        }
    })();

    const toggleReconcileGroup = (value) => setReconcileGroups((prev) => (
        prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
    ));

    const handleSubmit = async () => {
        if (!integration) {
            enqueueSnackbar('Please select an integration', { variant: 'warning' });
            return;
        }
        if (!sourceOk) {
            const messages = {
                updated: 'Please select an updated-from time',
                created: 'Please select a created-from time',
                products: 'Please provide product IDs and a complete travel date range',
                orders: 'Please provide at least one order ID',
                reconcile: 'Please select at least one order group',
            };
            enqueueSnackbar(messages[sourceMode] || 'Please select a source mode', { variant: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            const params = {
                integration_name: integration,
                account: account,
                ...(effectiveTopId ? { tour_operator: effectiveTopId } : {}),
                ...(sourceMode === 'updated' ? {
                    updated_from: updatedRange[0].toISOString(),
                    ...(updatedRange[1] ? { updated_to: updatedRange[1].toISOString() } : {}),
                } : {}),
                ...(sourceMode === 'created' ? {
                    created_from: createdRange[0].toISOString(),
                    ...(createdRange[1] ? { created_to: createdRange[1].toISOString() } : {}),
                } : {}),
                ...(sourceMode === 'products' ? {
                    product_ids: productIdList,
                    from_date: travelRange[0].format('YYYY-MM-DD'),
                    to_date: travelRange[1].format('YYYY-MM-DD'),
                } : {}),
                ...(sourceMode === 'orders' ? { order_ids: orderIds } : {}),
                ...(sourceMode === 'reconcile' ? {
                    reconcile_existing: true,
                    ...(oq?.reconcile_groups ? { reconcile_groups: reconcileGroups } : {}),
                } : {}),
                processing_mode: processingMode,
                send_notifications: sendNotifications,
                send_surveys: sendSurveys,
                dry_run: dryRun,
                approve_before_import: !dryRun && approveBeforeImport,
            };

            const res = await dataAPI(CREATE, 'bg_tasks', {
                urlSuffix: 'start_sync_orders/',
                data: params,
            });

            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
            } else if (res && res.statusCode >= 400) {
                enqueueSnackbar(
                    res.data?.message || 'Error starting order sync',
                    { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Error starting order sync', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Updated/Created windows share one layout — pick the active pair.
    const windowLabel = sourceMode === 'updated' ? 'Updated' : 'Created';
    const windowRange = sourceMode === 'updated' ? updatedRange : createdRange;
    const setWindowRange = sourceMode === 'updated' ? setUpdatedRange : setCreatedRange;

    return (
        <div className={classes.root}>
            <PageMeta title="Sync Orders" />
            <PageHeading sx={{ mb: 1 }}>Sync Orders</PageHeading>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Fetch orders from an integration and import them.
                The operation runs in the background — you can monitor progress
                via the background operations panel.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <LicenseeFormField
                        value={selectedLicensee}
                        onChange={setSelectedLicensee}
                        error={isFranchise && !selectedLicensee}
                        helperText={isFranchise && !selectedLicensee ? 'Please select a licensee' : undefined}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        select
                        fullWidth
                        label="Integration"
                        value={integration}
                        onChange={(e) => setIntegration(e.target.value)}
                        variant="outlined"
                        size="small"
                        required
                        disabled={integrations.length <= 1}
                        helperText={integrations.length === 0
                            ? 'No integrations with order sync support'
                            : undefined}
                    >
                        {integrations.map((intg) => (
                            <MenuItem key={intg.name} value={intg.name}>
                                {intg.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {accounts.length > 1 && (
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Account"
                            value={account ?? ''}
                            onChange={(e) => setAccount(e.target.value || null)}
                            variant="outlined"
                            size="small"
                            disabled={loading}
                            InputProps={{
                                endAdornment: loading ? <CircularProgress size={16} /> : null,
                            }}
                        >
                            {accounts.map((acc) => (
                                <MenuItem key={acc.slug ?? '__default'} value={acc.slug ?? ''}>
                                    {acc.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}

                {availableModes.length > 0 && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            Source
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={sourceMode}
                            onChange={(e, v) => v && setSourceMode(v)}
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {availableModes.map((m) => (
                                <ToggleButton key={m.value} value={m.value}>
                                    {m.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Grid>
                )}

                {(sourceMode === 'updated' || sourceMode === 'created') && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <DateTimePicker
                                label={`${windowLabel} from`}
                                value={windowRange[0]}
                                onChange={(v) => setWindowRange((p) => [v, p[1]])}
                                slotProps={{ textField: { size: 'small', sx: { width: 260 } } }}
                            />
                            <DateTimePicker
                                label={`${windowLabel} to`}
                                value={windowRange[1]}
                                onChange={(v) => setWindowRange((p) => [p[0], v])}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { width: 260 },
                                        helperText: 'Empty = up to now',
                                    },
                                }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', minHeight: 40 }}>
                                {UPDATED_PRESETS.map((r) => (
                                    <Chip
                                        key={r.label}
                                        size="small"
                                        label={r.label}
                                        variant="outlined"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                        onClick={() => {
                                            const now = moment();
                                            setWindowRange([now.clone().subtract(r.hours, 'hours'), now]);
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                )}

                {sourceMode === 'products' && (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                label="Product IDs"
                                value={productIds}
                                onChange={(e) => setProductIds(e.target.value)}
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={2}
                                maxRows={8}
                                fullWidth
                                required
                                helperText="One per line or comma-separated"
                                placeholder={'P001\nP002\nP003'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DateRangePicker
                                defaultStartDate={travelRange[0]}
                                daySpan={travelRange[1] ? travelRange[1].diff(travelRange[0], 'days') + 1 : 1}
                                onChange={(range) => setTravelRange(range)}
                            />
                            <Typography variant="caption" color="textSecondary">
                                Travel date range — both start and end are required
                            </Typography>
                        </Grid>
                    </>
                )}

                {sourceMode === 'orders' && (
                    <Grid item xs={12}>
                        {orderIds.length > IDS_COLLAPSE_THRESHOLD ? (
                            <Alert
                                severity="info"
                                action={(
                                    <Button color="inherit" size="small" onClick={() => setOrderIds([])}>
                                        Clear
                                    </Button>
                                )}
                                sx={{ mb: 1 }}
                            >
                                {orderIds.length} order IDs loaded.
                            </Alert>
                        ) : (
                            <Autocomplete
                                multiple
                                freeSolo
                                autoSelect
                                options={[]}
                                value={orderIds}
                                onChange={(e, v) => setOrderIds(
                                    Array.from(new Set(v.map((s) => String(s).trim()).filter(Boolean))),
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip size="small" label={option} {...getTagProps({ index })} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Order IDs"
                                        variant="outlined"
                                        size="small"
                                        placeholder="Type an ID and press Enter"
                                        helperText="Each ID becomes a badge"
                                    />
                                )}
                            />
                        )}
                        <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            sx={{ mt: 1 }}
                        >
                            Upload file (one ID per line)
                            <input hidden type="file" accept=".txt,.csv,text/plain" onChange={handleIdsFile} />
                        </Button>
                    </Grid>
                )}

                {sourceMode === 'reconcile' && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Refetches orders already stored locally for this
                            integration/account.
                        </Alert>
                        {oq?.reconcile_groups && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Order groups (by travel time; orders with no dated
                                    bookings count as past)
                                </Typography>
                                <FormGroup row>
                                    {RECONCILE_GROUPS.map((g) => (
                                        <FormControlLabel
                                            key={g.value}
                                            control={
                                                <Checkbox
                                                    checked={reconcileGroups.includes(g.value)}
                                                    onChange={() => toggleReconcileGroup(g.value)}
                                                />
                                            }
                                            label={g.label}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        )}
                    </Grid>
                )}

                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                            />
                        }
                        label="Dry run — fetch orders and build artifacts, do not import"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={approveBeforeImport && !dryRun}
                                disabled={dryRun}
                                onChange={(e) => setApproveBeforeImport(e.target.checked)}
                            />
                        }
                        label="Review fetched orders before processing"
                    />
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                        When checked, the task will pause after fetching and let you inspect
                        the raw response artifacts before proceeding with the import.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendNotifications}
                                onChange={(e) => setSendNotifications(e.target.checked)}
                            />
                        }
                        label="Send invitations to imported participants"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendSurveys}
                                onChange={(e) => setSendSurveys(e.target.checked)}
                            />
                        }
                        label="Send surveys to imported participants"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                        Processing mode
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={processingMode}
                            onChange={(e, v) => v && setProcessingMode(v)}
                        >
                            <ToggleButton value="queue">Queue</ToggleButton>
                            <ToggleButton value="inline">Inline</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="textSecondary">
                            {processingMode === 'queue'
                                ? 'Orders go through the import queue, serialized with live webhooks.'
                                : 'Orders are processed inline — per-order results appear in the task log.'}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                        onClick={handleSubmit}
                        disabled={submitting || !integration || !sourceOk || (isFranchise && !selectedLicensee)}
                    >
                        {dryRun ? 'Start Dry Run' : 'Start Sync'}
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default SyncOrdersPage;
